import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar, Download, AlertCircle, CheckCircle2, Loader2, Search } from "lucide-react";
import { useFacebookLeadsSync } from "@/hooks/useFacebookLeadsSync";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function FacebookLeadsSync() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [syncLogs, setSyncLogs] = useState([]);
  const [searchPhone, setSearchPhone] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [formIdInput, setFormIdInput] = useState("");
  const [isImportingPhone, setIsImportingPhone] = useState(false);
  const [listFormId, setListFormId] = useState("1122790469713415");
  const [formLeads, setFormLeads] = useState(null);
  const [isLoadingFormLeads, setIsLoadingFormLeads] = useState(false);
  const [isBulkReimporting, setIsBulkReimporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const { mutate: syncLeads, isPending, isSuccess, data } = useFacebookLeadsSync();

  const handleListFormLeads = async () => {
    if (!listFormId.trim()) return;
    setIsLoadingFormLeads(true);
    setFormLeads(null);
    try {
      const { data: result, error } = await supabase.functions.invoke('facebook-leads-import', {
        body: { 
          list_form_leads: true,
          form_id: listFormId.trim(),
          start_date: startDate || new Date().toISOString().split('T')[0],
          end_date: endDate || new Date().toISOString().split('T')[0]
        }
      });
      if (error) throw error;
      setFormLeads(result);
    } catch (err) {
      console.error('Error listing form leads:', err);
      setFormLeads({ error: String(err), leads: [] });
    } finally {
      setIsLoadingFormLeads(false);
    }
  };

  const handleBulkReimport = async () => {
    const incompleteLeads = formLeads?.leads?.filter(lead => lead.hasIncompleteData && lead.phone) || [];
    
    if (incompleteLeads.length === 0) {
      toast.error('Nenhum lead com dados incompletos encontrado');
      return;
    }

    setIsBulkReimporting(true);
    setBulkProgress({ current: 0, total: incompleteLeads.length });
    
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < incompleteLeads.length; i++) {
      const lead = incompleteLeads[i];
      setBulkProgress({ current: i + 1, total: incompleteLeads.length });
      
      try {
        const { error } = await supabase.functions.invoke('facebook-leads-import', {
          body: { search_phone: lead.phone, form_id: listFormId }
        });
        
        if (error) throw error;
        successCount++;
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        console.error(`Erro ao reimportar ${lead.name}:`, err);
        errorCount++;
      }
    }

    setIsBulkReimporting(false);
    setBulkProgress({ current: 0, total: 0 });
    
    toast.success(`Reimportação concluída: ${successCount} sucesso, ${errorCount} erros`);
    
    // Refresh the list
    await handleListFormLeads();
  };

  const handleSync = () => {
    if (!startDate || !endDate) {
      return;
    }
    setSyncLogs([`🔄 Iniciando sync de ${startDate} até ${endDate}...`]);
    setSearchResult(null);
    syncLeads({ startDate, endDate });
  };

  const handleSearchByPhone = async () => {
    if (!searchPhone.trim()) return;
    
    setIsSearching(true);
    setSearchResult(null);
    
    try {
      const { data: leads, error } = await supabase
        .from('leads')
        .select('id, first_name, last_name, primary_phone, created_at, facebook_lead_id, facebook_form_id')
        .eq('primary_phone', searchPhone.trim())
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (leads && leads.length > 0) {
        setSearchResult({
          found: true,
          inDatabase: true,
          leads: leads,
          message: `✅ Lead encontrado no banco de dados!`
        });
      } else {
        setSearchResult({
          found: false,
          inDatabase: false,
          message: `❌ Nenhum lead com telefone ${searchPhone} encontrado no banco. Verifique se o telefone está correto ou tente fazer sync do período em que ele foi criado no Facebook.`
        });
      }
    } catch (error) {
      console.error('Error searching lead:', error);
      setSearchResult({
        found: false,
        error: true,
        message: `Erro ao buscar: ${error.message}`
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Set default dates (last 30 days)
  const handleSetLast30Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    // Use local dates
    const formatLocal = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatLocal(end));
    setStartDate(formatLocal(start));
  };

  const handleSetLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    
    // Use local dates
    const formatLocal = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatLocal(end));
    setStartDate(formatLocal(start));
  };

  const handleSetToday = () => {
    // Use local date instead of UTC to avoid timezone issues
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const localDate = `${year}-${month}-${day}`;
    
    setStartDate(localDate);
    setEndDate(localDate);
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Sync Facebook Leads</h1>
        <p className="text-muted-foreground text-lg">
          Importe leads históricos do Facebook Lead Ads selecionando um período específico
        </p>
      </div>

      {/* Search by Phone */}
      <Card className="shadow-lg mb-6 border-primary/20">
        <CardHeader className="border-b bg-primary/5">
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Buscar Lead por Telefone
          </CardTitle>
          <CardDescription>
            Verifique se um lead específico já foi importado
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="+17865891358"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchByPhone()}
              />
            </div>
            <Button 
              onClick={handleSearchByPhone}
              disabled={!searchPhone.trim() || isSearching}
            >
              {isSearching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Buscar
                </>
              )}
            </Button>
          </div>

          {/* Import by phone controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Input
                placeholder="Form ID do Facebook (ex: 1122790469713415)"
                value={formIdInput}
                onChange={(e) => setFormIdInput(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                if (!searchPhone.trim() || !formIdInput.trim()) return;
                setIsImportingPhone(true);
                try {
                  const { data: rescue, error } = await supabase.functions.invoke('facebook-leads-import', {
                    body: { search_phone: searchPhone.trim(), form_id: formIdInput.trim() }
                  });
                  if (error) throw error;
                  setSyncLogs((prev) => [...prev, rescue?.message || 'Importação finalizada']);
                  // Refresh search in DB
                  await handleSearchByPhone();
                } catch (err) {
                  setSyncLogs((prev) => [...prev, `Erro ao importar por telefone: ${String(err)}`]);
                } finally {
                  setIsImportingPhone(false);
                }
              }}
              disabled={!searchPhone.trim() || !formIdInput.trim() || isImportingPhone}
            >
              {isImportingPhone ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Importar do Facebook
                </>
              )}
            </Button>
          </div>


          {searchResult && (
            <Alert className={searchResult.found ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800" : "bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800"}>
              {searchResult.found ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              )}
              <AlertDescription>
                <div className="font-medium mb-2">{searchResult.message}</div>
                {searchResult.leads && searchResult.leads.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {searchResult.leads.map((lead) => (
                      <div key={lead.id} className="p-3 bg-background rounded border">
                        <div className="font-medium">{lead.first_name} {lead.last_name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Telefone: {lead.primary_phone}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Criado em: {new Date(lead.created_at).toLocaleString('pt-BR')}
                        </div>
                        {lead.facebook_form_id && (
                          <div className="text-sm text-muted-foreground">
                            Form ID: {lead.facebook_form_id}
                          </div>
                        )}
                        {lead.facebook_lead_id && (
                          <div className="text-sm text-muted-foreground">
                            Lead ID FB: {lead.facebook_lead_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* List Leads from Specific Form */}
      <Card className="shadow-lg mb-6 border-blue-200 dark:border-blue-800">
        <CardHeader className="border-b bg-blue-50 dark:bg-blue-950">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Listar Leads de um Form Específico
          </CardTitle>
          <CardDescription>
            Veja todos os leads do Facebook de um formulário específico (direto da API do Facebook)
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="Form ID (ex: 1122790469713415)"
                value={listFormId}
                onChange={(e) => setListFormId(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleListFormLeads}
              disabled={!listFormId.trim() || isLoadingFormLeads}
              variant="default"
            >
              {isLoadingFormLeads ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Ver Leads do Form
                </>
              )}
            </Button>
          </div>

          {formLeads && (
            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription>
                <div className="flex justify-between items-center mb-3">
                  <div className="font-medium">
                    {formLeads.error ? (
                      <span className="text-red-600">Erro: {formLeads.error}</span>
                    ) : (
                      <span>📋 {formLeads.leads?.length || 0} leads encontrados no Facebook (período: {formLeads.date_range?.start} - {formLeads.date_range?.end})</span>
                    )}
                  </div>
                  {formLeads.leads && formLeads.leads.some(l => l.hasIncompleteData) && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={handleBulkReimport}
                      disabled={isBulkReimporting}
                      className="ml-2"
                    >
                      {isBulkReimporting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {bulkProgress.current}/{bulkProgress.total}
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Reimportar Todos ({formLeads.leads.filter(l => l.hasIncompleteData && l.phone).length})
                        </>
                      )}
                    </Button>
                  )}
                </div>
                {formLeads.leads && formLeads.leads.length > 0 && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {formLeads.leads.map((lead, idx) => (
                      <div key={idx} className="p-3 bg-background rounded border border-blue-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium">{lead.name}</div>
                            <div className="text-sm text-muted-foreground">ID: {lead.id}</div>
                          </div>
                          <div className="flex gap-2">
                            {lead.hasIncompleteData ? (
                              <Badge variant="destructive" className="text-xs">
                                ⚠️ Dados Incompletos
                              </Badge>
                            ) : lead.inDatabase ? (
                              <Badge variant="default" className="text-xs">
                                ✓ Importado
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                Não importado
                              </Badge>
                            )}
                          </div>
                        </div>
                        {lead.phone && <div className="text-sm">📞 {lead.phone}</div>}
                        {lead.email && <div className="text-sm">✉️ {lead.email}</div>}
                        {lead.created_time && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Criado: {new Date(lead.created_time).toLocaleString('pt-BR')}
                          </div>
                        )}
                        {lead.hasIncompleteData && lead.phone && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 w-full"
                            onClick={async () => {
                              try {
                                const { data, error } = await supabase.functions.invoke('facebook-leads-import', {
                                  body: { search_phone: lead.phone, form_id: listFormId }
                                });
                                if (error) throw error;
                                toast.success('Lead reimportado com sucesso!');
                                // Refresh list
                                await handleListFormLeads();
                              } catch (err) {
                                toast.error(`Erro ao reimportar: ${String(err)}`);
                              }
                            }}
                          >
                            🔄 Reimportar com Dados Completos
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg">
        <CardHeader className="border-b bg-muted/50">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Selecionar Período
          </CardTitle>
          <CardDescription>
            Escolha o intervalo de datas para importar leads do Facebook
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Quick Date Presets */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSetToday}
              className="text-sm"
            >
              Hoje
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSetLast7Days}
              className="text-sm"
            >
              Últimos 7 dias
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSetLast30Days}
              className="text-sm"
            >
              Últimos 30 dias
            </Button>
          </div>

          {/* Date Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-base font-medium">
                Data Inicial
              </Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                className="text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-base font-medium">
                Data Final
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                max={new Date().toISOString().split('T')[0]}
                className="text-base"
              />
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Importante:</strong> Apenas leads que ainda não foram importados serão adicionados.
              Leads duplicados serão automaticamente ignorados.
            </AlertDescription>
          </Alert>

          {/* Sync Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSync}
              disabled={!startDate || !endDate || isPending}
              size="lg"
              className="min-w-[200px]"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Iniciar Sync
                </>
              )}
            </Button>
          </div>

          {/* Success Result */}
          {isSuccess && data && (
            <>
              <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  <div className="space-y-2">
                    <div className="font-semibold text-lg">
                      ✓ Sync concluído com sucesso!
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Leads Importados</div>
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {data.imported}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Período</div>
                        <div className="text-sm font-medium">
                          {data.date_range?.start} até {data.date_range?.end}
                        </div>
                      </div>
                    </div>
                    {data.leads && data.leads.length > 0 && (
                      <div className="mt-4">
                        <div className="text-sm font-medium mb-2">Leads importados:</div>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {data.leads.slice(0, 10).map((lead, idx) => (
                            <div key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {lead.form}
                              </Badge>
                              <span>{lead.name}</span>
                              {lead.campaign && (
                                <span className="text-xs text-muted-foreground">
                                  ({lead.campaign})
                                </span>
                              )}
                            </div>
                          ))}
                          {data.leads.length > 10 && (
                            <div className="text-xs text-muted-foreground italic">
                              + {data.leads.length - 10} mais...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>

              {/* Forms Stats */}
              {data.forms && data.forms.length > 0 && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-base">📋 Estatísticas por Formulário</CardTitle>
                    <CardDescription>
                      Detalhamento de leads por cada formulário do Facebook
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.forms.map((form, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                          <div className="flex-1">
                            <div className="font-medium text-sm">{form.formName}</div>
                            <div className="text-xs text-muted-foreground mt-1">ID: {form.formId}</div>
                          </div>
                          <div className="flex gap-4 text-sm">
                            <div className="text-center">
                              <div className="text-muted-foreground text-xs">Total</div>
                              <div className="font-bold">{form.totalLeads}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-muted-foreground text-xs">Importados</div>
                              <div className="font-bold text-green-600">{form.imported}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-muted-foreground text-xs">Duplicados</div>
                              <div className="font-bold text-yellow-600">{form.totalLeads - form.imported}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* How it Works Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Como Funciona</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <Badge className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                1
              </Badge>
              <div>
                <div className="font-medium">Selecione o Período</div>
                <div className="text-sm text-muted-foreground">
                  Escolha as datas inicial e final para buscar leads do Facebook
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Badge className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                2
              </Badge>
              <div>
                <div className="font-medium">Busca Automática</div>
                <div className="text-sm text-muted-foreground">
                  O sistema busca todos os lead forms conectados e seus respectivos leads
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Badge className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                3
              </Badge>
              <div>
                <div className="font-medium">Importação Inteligente</div>
                <div className="text-sm text-muted-foreground">
                  Leads são importados com UTMs, atribuição de campanha e market detection automático
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Badge className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                4
              </Badge>
              <div>
                <div className="font-medium">Proteção contra Duplicatas</div>
                <div className="text-sm text-muted-foreground">
                  Leads já existentes são automaticamente ignorados pelo facebook_lead_id
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
